import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ListTransactionPerUserResponse } from '_business/types/Transactions';
import {
  GlobalSearch,
  GlobalPaginate,
  TransactionUrls,
  GlobalExport,
  GlobalFilter,
} from 'services/api/urls';
import { DetailsTransactionResponse } from '_business/types/TransactionDetails';
import { useGenericGetQuery } from '_technical/hooks/useGenericQuery';
import BaseService from 'services/api/BaseService';
import {
  DealDocument,
  DocumentsTransactionResponse,
} from '_business/types/TransactionsDocuments';
import { DownloadDocumentResponse } from '_business/types/DownloadDocumentResponse';
import { useState } from 'react';
import { message, notification } from 'antd';
import { useTranslation } from 'react-i18next';
import { TransactionAdminResponse } from '_business/types/TransactionAdminResponse';
import {
  CategoryDocument,
  DocumentCategory,
  MAX_DOCUMENT_SIZE_MB,
} from '_business/const/DocumentsCategories';
import { ListPartnerContactsDealsResponse } from '_business/types/TransactionsPartner';
import { useAuthContext } from '_technical/contexts/AuthContext';
import { Role } from '_business/types/User';
import { HubspotDealStatus } from '_business/const/ProjectSteep';

const TRANSACTIONS_LIST_KEY = 'TRANSACTIONS_LIST';

export const useTransactions = (page: number, per_page: number) => {
  const queryClient = useQueryClient();
  const url = TransactionUrls.LIST(page, per_page);
  queryClient.setQueryData([TRANSACTIONS_LIST_KEY], url);
  return useGenericGetQuery<ListTransactionPerUserResponse, Error>(url);
};

export const usePartnerTransactions = (
  options: GlobalPaginate & GlobalSearch & GlobalFilter,
) => {
  const url = TransactionUrls.PARTNER_TRANSACTIONS(options);
  return useGenericGetQuery<ListPartnerContactsDealsResponse, Error>(url);
};

export const usePartnerTransactionsDownload = (
  options: GlobalExport & {
    setIsDownloading?: (isDownloading: boolean) => void;
  } & GlobalSearch &
    GlobalFilter,
) => {
  const { t } = useTranslation();
  return async () => {
    options.setIsDownloading?.(true);
    try {
      const response = await BaseService.getRequest(
        TransactionUrls.PARTNER_TRANSACTIONS_DOWNLOAD({
          ...options,
        }),
        true,
      );
      const blob = await response.blob();
      if ([200, 201].includes(response.status)) {
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute('download', t('clients:export_filename'));
        link.click();
        link.remove();
      } else {
        notification.error({
          message: t('global:download_error'),
          closable: false,
        });
      }
    } catch (e: any) {
      notification.error({
        message: t('global:download_error'),
        closable: false,
      });
    }
    options.setIsDownloading?.(false);
  };
};

export const useTransactionDetails = (id: string) => {
  const url = TransactionUrls.DETAILS(id);
  return useGenericGetQuery<DetailsTransactionResponse, Error>(url);
};

export const useTransactionInterlocutors = (id: string) => {
  const { auth } = useAuthContext();

  const url = TransactionUrls.INTERLOCUTORS(id);
  return useGenericGetQuery<[TransactionAdminResponse], Error>(url, {
    staleTime: 60 * 60 * 1000,
    enabled: auth?.user?.role !== Role.PARTNER,
  });
};

export const useTransactionDocuments = (id: number | string) => {
  const { auth } = useAuthContext();

  const url = TransactionUrls.LIST_DOCUMENTS(id);
  return useGenericGetQuery<DocumentsTransactionResponse, Error>(url, {
    enabled: auth?.user?.role !== Role.PARTNER,
  });
};

export const useDownloadDocument = () => {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation('global');

  const download = (documentId: string) => {
    setLoading(true);
    BaseService.getRequest(TransactionUrls.DOWNLOAD_DOCUMENT(documentId), true)
      .then(async (response) => {
        if (response.status === 200 || response.status === 201) {
          const data: DownloadDocumentResponse = await response.json();

          // Fetch the file as blob to handle cross-origin downloads
          try {
            const fileResponse = await fetch(data.url);
            const blob = await fileResponse.blob();

            // Create blob URL and download
            const blobUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = data.name;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            // Clean up blob URL
            URL.revokeObjectURL(blobUrl);
          } catch (fetchError) {
            // Fallback to direct URL if blob fetch fails
            const a = document.createElement('a');
            a.href = data.url;
            a.download = data.name;
            a.click();
          }
        } else {
          const data = await response.json();
          message.warning(data?.message || t('internet_connexion_error'));
        }
      })
      .catch(() => {
        notification.error({ message: t('internet_connexion_error') });
      })
      .finally(() => setLoading(false));
  };

  return {
    loading,
    download,
  };
};

interface UploadDocumentPayload {
  files: File[];
  category: CategoryDocument;
  transactionId: string | number;
  previewsDocumentId?: string | number;
}

export const useUploadFile = () => {
  const { t } = useTranslation('global');
  const queryClient = useQueryClient();
  return useMutation<DealDocument[], Error, UploadDocumentPayload>({
    mutationFn: async ({
      files,
      transactionId,
      category,
      previewsDocumentId,
    }) => {
      files.forEach((file) => {
        if (file.size > 1024 * 1024 * MAX_DOCUMENT_SIZE_MB) {
          throw new Error(t('max_file', { size: MAX_DOCUMENT_SIZE_MB }));
        }
      });

      const formData = new FormData();
      files.forEach((file) => {
        formData.append(`files`, file);
      });

      if (previewsDocumentId) {
        formData.append('previewsDocumentId', previewsDocumentId.toString());
      }

      const response = await BaseService.putFileRequest(
        TransactionUrls.UPLOAD(transactionId, category),
        formData,
        true,
      );

      const data: DealDocument[] = await response.json();

      if ([200, 201].includes(response.status)) {
        // On met a jour les détails de la transaction
        queryClient.setQueryData<DocumentsTransactionResponse>(
          [TransactionUrls.LIST_DOCUMENTS(transactionId)],
          (oldData) => {
            if (!oldData) return oldData;
            const deal = oldData.deal.CRM.deal;
            const filesIds = deal[category]?.split(';') ?? [];
            const newFilesIds = data.map((file) => file.id);
            const updatedFilesIds = [...filesIds, ...newFilesIds].join(';');
            return {
              files: [...oldData.files, ...data],
              deal: {
                ...oldData.deal,
                CRM: {
                  ...oldData.deal.CRM,
                  deal: { ...deal, [category]: updatedFilesIds },
                },
              },
            } as DocumentsTransactionResponse;
          },
        );

        // On met a jour la liste des transactions
        const currentListUrl = queryClient.getQueryData<string>([
          TRANSACTIONS_LIST_KEY,
        ]);
        if (currentListUrl) {
          queryClient.setQueryData<ListTransactionPerUserResponse>(
            [currentListUrl],
            (oldData) => {
              if (!oldData) return oldData;

              // On trouve et met à jour seulement le deal spécifique
              const dealIndex =
                oldData.CRM.contact.associations.deal_collection__contact_to_deal.items.findIndex(
                  (deal) =>
                    deal.hs_object_id.toString() === transactionId.toString(),
                );

              if (dealIndex === -1) return oldData; // Deal non trouvé

              const currentDeal =
                oldData.CRM.contact.associations
                  .deal_collection__contact_to_deal.items[dealIndex];

              // On met à jour les fichiers pour cette catégorie
              const filesIds =
                currentDeal[category]?.split(';').filter((id) => id) ?? [];
              const newFilesIds = data.map((file) => file.id);
              const updatedFilesIds = [...filesIds, ...newFilesIds].join(';');

              const updatedDeal = {
                ...currentDeal,
                [category]: updatedFilesIds,
              };

              // Si c'est une facture HI, on met à jour la sous-étape
              if (category === DocumentCategory.FACTURES_HI) {
                updatedDeal.sous_etape_hi = {
                  label: HubspotDealStatus.FacturesRecuesAControler,
                  value: HubspotDealStatus.FacturesRecuesAControler,
                };
              }

              const updatedDeals = [
                ...oldData.CRM.contact.associations
                  .deal_collection__contact_to_deal.items,
              ];
              updatedDeals[dealIndex] = updatedDeal;

              return {
                ...oldData,
                CRM: {
                  ...oldData.CRM,
                  contact: {
                    ...oldData.CRM.contact,
                    associations: {
                      ...oldData.CRM.contact.associations,
                      deal_collection__contact_to_deal: {
                        ...oldData.CRM.contact.associations
                          .deal_collection__contact_to_deal,
                        items: updatedDeals,
                      },
                    },
                  },
                },
              };
            },
          );
        }
        // On met a jour la sous-étape de la transaction si c'est une facture HI
        if (category === DocumentCategory.FACTURES_HI) {
          queryClient.setQueryData<DetailsTransactionResponse>(
            [TransactionUrls.DETAILS(transactionId.toString())],
            (oldData) => {
              if (!oldData) return oldData;
              return {
                ...oldData,
                deal: {
                  ...oldData.deal,
                  CRM: {
                    ...oldData.deal.CRM,
                    deal: {
                      ...oldData.deal.CRM.deal,
                      sous_etape_hi: {
                        label: HubspotDealStatus.FacturesRecuesAControler,
                        value: HubspotDealStatus.FacturesRecuesAControler,
                      },
                    },
                  },
                },
              } as DetailsTransactionResponse;
            },
          );
        }

        notification.success({
          message: t('files_uploaded', { count: files.length }),
          closable: false,
        });

        return data;
      } else {
        throw new Error(t('internet_connexion_error'));
      }
    },
    onError: (error) => {
      notification.error({
        message: t(error.message),
        duration: 8,
        closable: false,
      });
    },
  });
};

export const useSelectScenario = (transactionId: string | number) => {
  const { t } = useTranslation('global');
  const queryClient = useQueryClient();

  return useMutation<
    { success: boolean; scenarioID: string },
    Error,
    { scenarioID: string }
  >({
    mutationFn: async ({ scenarioID }) => {
      const response = await BaseService.postRequest(
        TransactionUrls.SELECT_SCENARIO(transactionId),
        { scenarioID },
        true,
      );

      if ([200, 201].includes(response.status)) {
        return await response.json();
      } else {
        throw new Error(t('internet_connexion_error'));
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData<DetailsTransactionResponse>(
        [TransactionUrls.DETAILS(transactionId.toString())],
        (oldData) => {
          if (!oldData) return oldData;

          const updatedData = { ...oldData };

          if (
            updatedData.deal?.CRM?.deal?.associations
              ?.p_projets_collection__projets_to_deal?.items
          ) {
            updatedData.deal.CRM.deal.associations.p_projets_collection__projets_to_deal.items =
              updatedData.deal.CRM.deal.associations.p_projets_collection__projets_to_deal.items.map(
                (item) => ({
                  ...item,
                  reference_du_scenario_retenu: data.scenarioID,
                }),
              );
          }

          return updatedData;
        },
      );
    },
    onError: (error) => {
      notification.error({
        message: t(error.message),
        duration: 8,
      });
    },
  });
};
